<template>
    <div class="event-image-overlay">
        <div v-if="date" class="event-image-overlay__info">
            <template v-if="isUpcomingEvent">
                <div class="event-image-overlay__date">
                    <Calendar class="event-image-overlay__date-icon" />
                    <span>{{ formattedDate }}</span>
                </div>
                <span class="event-image-overlay__delay">
                    <template v-if="isSameDay(new Date(date), new Date())">
                        {{ t('resources.events.today') }}
                    </template>
                    <template v-else-if="getDaysDifference(new Date(date), new Date()) === 1">
                        {{ t('resources.events.tomorrow') }}
                    </template>
                    <template v-else>
                        {{ t('resources.events.inDays', [getDaysDifference(new Date(date), new Date()).toString()]) }}
                    </template>
                </span>
            </template>

            <div v-else-if="!isUpcomingEvent" class="event-image-overlay__replay-date">
                <span>
                    {{ t('resources.events.replayDate') }}
                </span>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import type {DateField} from '@prismicio/types';
    import {useTranslation} from '#imports';
    import {useFormat} from '#imports';
    import Calendar from '~/components/icons/Calendar.vue';
    import {createDateHelpers} from '~/composables/dateHelpers';

    const props = defineProps<{date: DateField; isUpcomingEvent: boolean}>();

    const {formatDate} = useFormat();
    const formattedDate = formatDate(props.date, {year: 'numeric', month: 'long', day: 'numeric'});

    const {t} = useTranslation();
    const {isSameDay, getDaysDifference} = createDateHelpers();
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .event-image-overlay {
        position: absolute;
        top: -3px;
        left: 0;
        background-color: var(--joy-color-neutral-70);
        z-index: 1;
        width: 220px;
        height: 72px;
        border-radius: 0 0 100px;
        transform: rotate(-4deg);

        &__info {
            position: absolute;
            top: 15px;
            left: 25px;
            z-index: 3;
            display: flex;
            flex-direction: column;
            transform: rotate(4deg);

            * {
                font-family: var(--joy-font-family-title);
            }
        }

        &__date,
        &__replay-date {
            color: var(--joy-color-neutral-0);
            font-weight: var(--joy-font-weight-bold);
            line-height: 100%;
        }

        &__date {
            font-size: var(--joy-font-size-primary-400);

            &-icon {
                width: 15px;
                height: 15px;
                margin-right: var(--joy-core-spacing-2);
                position: relative;
                top: 3px;
            }

            * {
                fill: var(--joy-color-neutral-0);
            }
        }

        &__replay-date {
            position: relative;
            top: 5px;
            font-size: var(--joy-font-size-primary-800);
        }

        &__delay {
            margin-top: var(--joy-core-spacing-0);
            color: var(--joy-color-quaternary-50);
            font-size: var(--joy-font-size-primary-300);
            margin-left: 23px;
        }
    }
</style>
